import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  Snackbar,
  Fade,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Lock,
  PersonAdd,
  Visibility,
  VisibilityOff,
  CloudUpload,
  VideoLibrary,
  DeleteSweep,
  Settings,
  AdminPanelSettings,
  CheckCircle,
  Cancel,
  Refresh,
  History,
  DriveFileRenameOutline,
  Close,
} from "@mui/icons-material";
import { userService } from "../services/api";

// Mapa de permisos para mostrar en la UI
const PERMISSION_MAP = {
  upload_videos: { label: "Subir Videos", icon: <CloudUpload fontSize="small" />, color: "info" },
  view_videos: { label: "Ver Videos", icon: <VideoLibrary fontSize="small" />, color: "success" },
  delete_videos: { label: "Eliminar Videos", icon: <DeleteSweep fontSize="small" />, color: "error" },
  settings: { label: "Configuración", icon: <Settings fontSize="small" />, color: "warning" },
  manage_users: { label: "Gestión Usuarios", icon: <AdminPanelSettings fontSize="small" />, color: "primary" },
};

// Estado inicial del formulario de usuario
const INITIAL_FORM_STATE = {
  username: "",
  password: "",
  displayName: "",
  role: "user",
  isActive: true,
  permissions: {
    upload_videos: false,
    view_videos: true,
    delete_videos: false,
    settings: false,
    manage_users: false,
  },
};

// Generar color de avatar basado en el string
const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 65%, 45%)`;
};

const UserManagement = ({ currentUser }) => {
  // Estado principal
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Estado del modal de crear/editar
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // null = crear, objeto = editar
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Estado del modal de cambiar contraseña
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Estado del dialog de confirmación de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Estado del modal de logs
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsUser, setLogsUser] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [deleting, setDeleting] = useState(false);

  // Cargar usuarios
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await userService.getUsers();
      if (response.success) {
        setUsers(response.users || []);
      }
    } catch (err) {
      setError(err.message || "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // =====================
  // CREAR / EDITAR USUARIO
  // =====================

  const handleOpenCreateModal = () => {
    setEditingUser(null);
    setFormData(INITIAL_FORM_STATE);
    setFormErrors({});
    setShowPassword(false);
    setModalOpen(true);
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: "", // No se muestra la contraseña actual
      displayName: user.displayName,
      role: user.role,
      isActive: user.isActive,
      permissions: { ...user.permissions },
    });
    setFormErrors({});
    setShowPassword(false);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormData(INITIAL_FORM_STATE);
    setFormErrors({});
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Si el rol cambia a admin, forzar todos los permisos a true
      if (field === "role" && value === "admin") {
        updated.permissions = {
          upload_videos: true,
          view_videos: true,
          delete_videos: true,
          settings: true,
          manage_users: true,
        };
      }

      // Si el rol cambia a user, quitar manage_users
      if (field === "role" && value === "user") {
        updated.permissions = {
          ...prev.permissions,
          manage_users: false,
        };
      }

      return updated;
    });

    // Limpiar error del campo al escribir
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePermissionChange = (permission, checked) => {
    setFormData((prev) => {
      const newPermissions = { ...prev.permissions, [permission]: checked };

      // Si desmarcamos view_videos, también desmarcar delete_videos
      if (permission === "view_videos" && !checked) {
        newPermissions.delete_videos = false;
      }

      return { ...prev, permissions: newPermissions };
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!editingUser) {
      // Validaciones solo para creación
      if (!formData.username.trim()) {
        errors.username = "El nombre de usuario es obligatorio";
      } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username.trim())) {
        errors.username = "Solo letras, números, guiones y guiones bajos";
      }

      if (!formData.password) {
        errors.password = "La contraseña es obligatoria";
      } else if (formData.password.length < 6) {
        errors.password = "Mínimo 6 caracteres";
      }
    }

    if (!formData.displayName.trim()) {
      errors.displayName = "El nombre para mostrar es obligatorio";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError("");

      if (editingUser) {
        // Editar usuario existente
        const updateData = {
          displayName: formData.displayName,
          role: formData.role,
          isActive: formData.isActive,
          permissions: formData.permissions,
        };
        const response = await userService.updateUser(editingUser.id, updateData);
        if (response.success) {
          setSuccess(`Usuario "${formData.displayName}" actualizado correctamente`);
          handleCloseModal();
          await loadUsers();
        }
      } else {
        // Crear usuario nuevo
        const createData = {
          username: formData.username.trim().toLowerCase(),
          password: formData.password,
          displayName: formData.displayName.trim(),
          role: formData.role,
          isActive: formData.isActive,
          permissions: formData.permissions,
        };
        const response = await userService.createUser(createData);
        if (response.success) {
          setSuccess(`Usuario "${formData.displayName}" creado correctamente`);
          handleCloseModal();
          await loadUsers();
        }
      }
    } catch (err) {
      setError(err.message || "Error guardando usuario");
    } finally {
      setSaving(false);
    }
  };

  // =====================
  // CAMBIAR CONTRASEÑA
  // =====================

  const handleOpenPasswordModal = (user) => {
    setPasswordUser(user);
    setNewPassword("");
    setShowNewPassword(false);
    setPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordUser(null);
    setNewPassword("");
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    try {
      setChangingPassword(true);
      setError("");
      const response = await userService.changePassword(passwordUser.id, newPassword);
      if (response.success) {
        setSuccess(`Contraseña de "${passwordUser.displayName}" cambiada correctamente`);
        handleClosePasswordModal();
      }
    } catch (err) {
      setError(err.message || "Error cambiando contraseña");
    } finally {
      setChangingPassword(false);
    }
  };

  // =====================
  // ELIMINAR USUARIO
  // =====================

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      setError("");
      const response = await userService.deleteUser(userToDelete.id);
      if (response.success) {
        setSuccess(`Usuario "${userToDelete.displayName}" eliminado correctamente`);
        handleCloseDeleteDialog();
        await loadUsers();
      }
    } catch (err) {
      setError(err.message || "Error eliminando usuario");
    } finally {
      setDeleting(false);
    }
  };

  // =====================
  // LOGS DE ACTIVIDAD
  // =====================

  const handleOpenLogsModal = async (user) => {
    setLogsUser(user);
    setLogsPage(0);
    setLogsModalOpen(true);
    await loadUserLogs(user.id, 0, logsPerPage);
  };

  const handleCloseLogsModal = () => {
    setLogsModalOpen(false);
    setLogsUser(null);
    setUserLogs([]);
    setLogsTotal(0);
    setLogsPage(0);
  };

  const loadUserLogs = async (userId, page, perPage) => {
    try {
      setLogsLoading(true);
      const response = await userService.getUserLogs(userId, {
        limit: perPage,
        offset: page * perPage,
      });
      if (response.success) {
        setUserLogs(response.data || []);
        setLogsTotal(response.total || 0);
      }
    } catch (err) {
      setError(err.message || "Error cargando logs de actividad");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLogsPageChange = (event, newPage) => {
    setLogsPage(newPage);
    if (logsUser) {
      loadUserLogs(logsUser.id, newPage, logsPerPage);
    }
  };

  const handleLogsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setLogsPerPage(newPerPage);
    setLogsPage(0);
    if (logsUser) {
      loadUserLogs(logsUser.id, 0, newPerPage);
    }
  };

  // Mapa de acciones para mostrar en la tabla de logs
  const ACTION_LABELS = {
    video_upload: { label: "Subió video", color: "info", icon: <CloudUpload fontSize="small" /> },
    video_delete: { label: "Eliminó video", color: "error", icon: <DeleteSweep fontSize="small" /> },
    video_rename: { label: "Renombró video", color: "warning", icon: <DriveFileRenameOutline fontSize="small" /> },
  };

  const formatLogDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatLogTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Verificar si es el usuario actual
  const isCurrentUser = (user) => user.id === currentUser?.id;

  // Contar admins activos
  const activeAdminCount = users.filter((u) => u.role === "admin" && u.isActive).length;
  const isLastAdmin = (user) => user.role === "admin" && activeAdminCount <= 1;

  // =====================
  // RENDER
  // =====================

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Cargando usuarios...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Mensajes de error/éxito */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Fade}
      >
        <Alert onClose={() => setSuccess("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {success}
        </Alert>
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Acciones superiores */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {users.length} usuario{users.length !== 1 ? "s" : ""} registrado{users.length !== 1 ? "s" : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Recargar lista">
            <IconButton size="small" onClick={loadUsers} disabled={loading}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAdd />}
            onClick={handleOpenCreateModal}
            sx={{
              px: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fdb 0%, #6a4293 100%)",
              },
            }}
          >
            Nuevo Usuario
          </Button>
        </Box>
      </Box>

      {/* Tabla de usuarios */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
          "& .MuiTableCell-root": {
            borderColor: "divider",
          },
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-head": {
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "text.secondary",
                  py: 1.5,
                },
              }}
            >
              <TableCell>Usuario</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Permisos</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                sx={{
                  "&:hover": { bgcolor: "action.hover" },
                  transition: "background-color 0.15s ease",
                  ...(isCurrentUser(user) && {
                    borderLeft: "3px solid",
                    borderLeftColor: "primary.main",
                  }),
                }}
              >
                {/* Usuario */}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: user.role === "admin"
                          ? "primary.main"
                          : stringToColor(user.displayName || user.username),
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      {(user.displayName || user.username).charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {user.displayName}
                        {isCurrentUser(user) && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 0.5, color: "primary.main", fontWeight: 400 }}
                          >
                            (tú)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                        @{user.username}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* Rol */}
                <TableCell>
                  <Chip
                    label={user.role === "admin" ? "Admin" : "Usuario"}
                    color={user.role === "admin" ? "primary" : "default"}
                    size="small"
                    variant={user.role === "admin" ? "filled" : "outlined"}
                    icon={user.role === "admin" ? <AdminPanelSettings fontSize="small" /> : undefined}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                </TableCell>

                {/* Estado */}
                <TableCell>
                  <Chip
                    label={user.isActive ? "Activo" : "Inactivo"}
                    color={user.isActive ? "success" : "default"}
                    size="small"
                    variant={user.isActive ? "filled" : "outlined"}
                    icon={
                      user.isActive ? (
                        <CheckCircle fontSize="small" />
                      ) : (
                        <Cancel fontSize="small" />
                      )
                    }
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.7rem",
                    }}
                  />
                </TableCell>

                {/* Permisos */}
                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {user.role === "admin" ? (
                      <Chip
                        label="Todos"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem", height: 22 }}
                      />
                    ) : (
                      Object.entries(user.permissions || {}).map(([key, value]) => {
                        if (!value || key === "manage_users") return null;
                        const permInfo = PERMISSION_MAP[key];
                        if (!permInfo) return null;
                        return (
                          <Chip
                            key={key}
                            label={permInfo.label}
                            size="small"
                            color={permInfo.color}
                            variant="outlined"
                            sx={{ fontSize: "0.65rem", height: 22 }}
                          />
                        );
                      })
                    )}
                  </Box>
                </TableCell>

                {/* Acciones */}
                <TableCell align="right">
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                    <Tooltip title="Editar usuario">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditModal(user)}
                        sx={{
                          color: "primary.main",
                          "&:hover": { bgcolor: "primary.main", color: "white" },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Cambiar contraseña">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPasswordModal(user)}
                        sx={{
                          color: "warning.main",
                          "&:hover": { bgcolor: "warning.main", color: "white" },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Lock fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Ver actividad">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenLogsModal(user)}
                        sx={{
                          color: "secondary.main",
                          "&:hover": { bgcolor: "secondary.main", color: "white" },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <History fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={
                        isCurrentUser(user)
                          ? "No puedes eliminar tu propio usuario"
                          : isLastAdmin(user)
                            ? "No puedes eliminar el último admin"
                            : "Eliminar usuario"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(user)}
                          disabled={isCurrentUser(user) || isLastAdmin(user)}
                          sx={{
                            color: "error.main",
                            "&:hover": { bgcolor: "error.main", color: "white" },
                            transition: "all 0.2s ease",
                            "&.Mui-disabled": { opacity: 0.3 },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No hay usuarios registrados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ============================= */}
      {/* MODAL: Crear / Editar Usuario */}
      {/* ============================= */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: "background.paper",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {editingUser ? <Edit color="primary" /> : <PersonAdd color="primary" />}
          <Typography variant="h6" fontWeight={600}>
            {editingUser ? `Editar: ${editingUser.displayName}` : "Crear Nuevo Usuario"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            {/* Username (solo en creación) */}
            {!editingUser && (
              <TextField
                id="user-form-username"
                label="Nombre de usuario"
                value={formData.username}
                onChange={(e) => handleFormChange("username", e.target.value)}
                error={!!formErrors.username}
                helperText={formErrors.username || "Solo letras, números, guiones y guiones bajos"}
                fullWidth
                autoFocus
                size="small"
                InputProps={{
                  sx: { fontFamily: "monospace" },
                }}
              />
            )}

            {/* Contraseña (solo en creación) */}
            {!editingUser && (
              <TextField
                id="user-form-password"
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleFormChange("password", e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password || "Mínimo 6 caracteres"}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Nombre para mostrar */}
            <TextField
              id="user-form-displayname"
              label="Nombre para mostrar"
              value={formData.displayName}
              onChange={(e) => handleFormChange("displayName", e.target.value)}
              error={!!formErrors.displayName}
              helperText={formErrors.displayName}
              fullWidth
              size="small"
              autoFocus={!!editingUser}
            />

            {/* Rol */}
            <FormControl fullWidth size="small">
              <InputLabel>Rol</InputLabel>
              <Select
                id="user-form-role"
                value={formData.role}
                onChange={(e) => handleFormChange("role", e.target.value)}
                label="Rol"
              >
                <MenuItem value="admin">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AdminPanelSettings fontSize="small" color="primary" />
                    Administrador
                  </Box>
                </MenuItem>
                <MenuItem value="user">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VideoLibrary fontSize="small" />
                    Usuario
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Estado */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleFormChange("isActive", e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {formData.isActive ? "Activo" : "Inactivo"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isActive
                      ? "El usuario puede iniciar sesión"
                      : "El usuario no podrá iniciar sesión"}
                  </Typography>
                </Box>
              }
            />

            {/* Permisos */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                bgcolor: formData.role === "admin" ? "action.hover" : "transparent",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Permisos
              </Typography>

              {formData.role === "admin" && (
                <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0 }}>
                  <Typography variant="caption">
                    Los administradores tienen todos los permisos automáticamente
                  </Typography>
                </Alert>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {/* Subir Videos */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.permissions.upload_videos}
                      onChange={(e) => handlePermissionChange("upload_videos", e.target.checked)}
                      disabled={formData.role === "admin"}
                      size="small"
                      color="info"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CloudUpload fontSize="small" color="info" />
                      <Typography variant="body2">Subir Videos</Typography>
                    </Box>
                  }
                />

                {/* Ver Videos */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.permissions.view_videos}
                      onChange={(e) => handlePermissionChange("view_videos", e.target.checked)}
                      disabled={formData.role === "admin"}
                      size="small"
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <VideoLibrary fontSize="small" color="success" />
                      <Typography variant="body2">Ver Videos</Typography>
                    </Box>
                  }
                />

                {/* Eliminar Videos (indentado, solo si view_videos está marcado) */}
                {formData.permissions.view_videos && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.delete_videos}
                        onChange={(e) => handlePermissionChange("delete_videos", e.target.checked)}
                        disabled={formData.role === "admin"}
                        size="small"
                        color="error"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <DeleteSweep fontSize="small" color="error" />
                        <Typography variant="body2">Eliminar Videos</Typography>
                      </Box>
                    }
                    sx={{ ml: 3 }}
                  />
                )}

                {/* Configuración */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.permissions.settings}
                      onChange={(e) => handlePermissionChange("settings", e.target.checked)}
                      disabled={formData.role === "admin"}
                      size="small"
                      color="warning"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Settings fontSize="small" color="warning" />
                      <Typography variant="body2">Configuración</Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={handleCloseModal} disabled={saving} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveUser}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : editingUser ? <Edit /> : <Add />}
            sx={{
              px: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fdb 0%, #6a4293 100%)",
              },
            }}
          >
            {saving ? "Guardando..." : editingUser ? "Guardar Cambios" : "Crear Usuario"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================= */}
      {/* MODAL: Cambiar Contraseña */}
      {/* ================================= */}
      <Dialog
        open={passwordModalOpen}
        onClose={handleClosePasswordModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Lock color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Cambiar Contraseña
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nueva contraseña para <strong>{passwordUser?.displayName}</strong> (@{passwordUser?.username})
          </Typography>

          <TextField
            id="change-password-field"
            label="Nueva contraseña"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            autoFocus
            size="small"
            helperText="Mínimo 6 caracteres"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={handleClosePasswordModal} disabled={changingPassword} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={changingPassword || !newPassword || newPassword.length < 6}
            startIcon={changingPassword ? <CircularProgress size={16} color="inherit" /> : <Lock />}
            color="warning"
          >
            {changingPassword ? "Cambiando..." : "Cambiar Contraseña"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================= */}
      {/* DIALOG: Confirmar Eliminación */}
      {/* ================================= */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Delete color="error" />
          <Typography variant="h6" fontWeight={600}>
            Confirmar Eliminación
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            Esta acción no se puede deshacer
          </Alert>
          <Typography variant="body2">
            ¿Estás seguro de que deseas eliminar al usuario{" "}
            <strong>{userToDelete?.displayName}</strong> (@{userToDelete?.username})?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            El usuario perderá el acceso al sistema inmediatamente.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? "Eliminando..." : "Eliminar Usuario"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================= */}
      {/* MODAL: Logs de Actividad */}
      {/* ================================= */}
      <Dialog
        open={logsModalOpen}
        onClose={handleCloseLogsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <History color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              Actividad de {logsUser?.displayName}
            </Typography>
            <Chip
              label={`@${logsUser?.username}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem" }}
            />
          </Box>
          <IconButton size="small" onClick={handleCloseLogsModal}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {logsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Cargando actividad...
              </Typography>
            </Box>
          ) : userLogs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <History sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Sin actividad registrada
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Las acciones del usuario aparecerán aquí
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-head": {
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          color: "text.secondary",
                          py: 1.5,
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <TableCell>Fecha</TableCell>
                      <TableCell>Hora</TableCell>
                      <TableCell>Acción</TableCell>
                      <TableCell>Video</TableCell>
                      <TableCell>Detalles</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userLogs.map((log) => {
                      const actionInfo = ACTION_LABELS[log.action] || {
                        label: log.action,
                        color: "default",
                        icon: null,
                      };
                      return (
                        <TableRow
                          key={log.id}
                          sx={{
                            "&:hover": { bgcolor: "action.hover" },
                            transition: "background-color 0.15s ease",
                          }}
                        >
                          {/* Fecha */}
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              {formatLogDate(log.timestamp)}
                            </Typography>
                          </TableCell>

                          {/* Hora */}
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              {formatLogTime(log.timestamp)}
                            </Typography>
                          </TableCell>

                          {/* Acción */}
                          <TableCell>
                            <Chip
                              icon={actionInfo.icon}
                              label={actionInfo.label}
                              color={actionInfo.color}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                            />
                          </TableCell>

                          {/* Nombre del Video */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                maxWidth: 220,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={log.details?.videoName || log.details?.fileName || "—"}
                            >
                              {log.details?.videoName || log.details?.fileName || "—"}
                            </Typography>
                          </TableCell>

                          {/* Detalles adicionales */}
                          <TableCell>
                            {log.action === "video_rename" && log.details?.previousName ? (
                              <Typography variant="caption" color="text.secondary">
                                Antes: {log.details.previousName}
                              </Typography>
                            ) : log.action === "video_upload" && log.details?.sala ? (
                              <Typography variant="caption" color="text.secondary">
                                Sala {log.details.sala} — {log.details.fecha}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginación */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {logsTotal} registro{logsTotal !== 1 ? "s" : ""}
                  </Typography>
                  <Select
                    value={logsPerPage}
                    onChange={handleLogsPerPageChange}
                    size="small"
                    variant="standard"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary">
                    Pág. {logsPage + 1} de {Math.max(1, Math.ceil(logsTotal / logsPerPage))}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      disabled={logsPage === 0}
                      onClick={(e) => handleLogsPageChange(e, logsPage - 1)}
                    >
                      <Typography variant="caption">←</Typography>
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={logsPage >= Math.ceil(logsTotal / logsPerPage) - 1}
                      onClick={(e) => handleLogsPageChange(e, logsPage + 1)}
                    >
                      <Typography variant="caption">→</Typography>
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
